import type { Picker } from "@libs/platform-wire/purchase";
import type { OptionSet } from "../options";

/** What a product is chosen by, in the sheet's "Choose your …": its
 *  option names in copy, each once ("Size"; "Colour and size", the rest
 *  lower case), as the server words its listings; null for one chosen by
 *  rows, and for names too long to say. */
export function optionLabelOfPicker(picker: Picker): string | null {
  switch (picker.kind) {
    case "rows":
      return null;
    case "options": {
      const seen = new Set<string>();
      const names = picker.names
        .map((name) => name.trim())
        .filter((name) => name && !seen.has(name.toLowerCase()) && seen.add(name.toLowerCase()))
        .map((name, index) => (index ? name.toLowerCase() : name));
      const label = names.length < 2 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
      return label && label.length <= 60 ? label : null;
    }
  }
}

/** The option set the sheet's dropdowns draw for a purchase chosen by its
 *  options (LIS-1173), as the picker's schema has already checked it (one
 *  part and one value per option, each combination once, one on sale);
 *  null for one chosen by rows. */
export function optionSetOfPicker(picker: Picker): OptionSet | null {
  switch (picker.kind) {
    case "rows":
      return null;
    case "options": {
      const choices = picker.items.map((item) => ({
        id: item.id,
        options: item.values,
        available: item.available,
        minQuantity: item.min,
        maxQuantity: item.max ?? Infinity,
      }));
      const values = picker.names.map((_, index) => {
        const own: string[] = [];
        for (const choice of choices) if (!own.includes(choice.options[index]!)) own.push(choice.options[index]!);
        return own;
      });
      return { names: picker.names, parts: picker.parts, values, choices };
    }
  }
}
